import { SlotSummaryCard, formatSessionTime } from "@codel/dova-ds";
import { HOME_HUB_COPY } from "../../home/homeHubCopy";

interface FirstSessionModuleProps {
  /** ISO timestamp of the first session, or null when not yet booked. */
  firstSessionAt: string | null;
  therapistName: string;
  /** Null on a legacy couple, whose source carries no length. */
  durationMinutes?: number | null;
  /** The server's code, not a label. Null before the couple has a session. */
  location?: string | null;
  /** The office address. Shown under an in-person session only. */
  locationAddress?: string | null;
}

/**
 * The server's location CODE, in this app's words.
 *
 * A map and not a formatter, so an unrecognised code renders NOTHING rather than a
 * guess or a raw `video_call` leaking onto a client's screen.
 *
 * Sentence case, where the therapist's own Select reads "Video Call": that is an
 * option label in a control, and this is prose in a sentence about their session.
 */
const LOCATION_LABEL: Record<string, string> = {
  video_call: "Video call",
  in_person: HOME_HUB_COPY.sessionLocationInPerson,
};

/** "50 min · Video call", with either half dropped when the server did not send it.
 *  Returns undefined when neither is known, which is `SlotSummary.secondary`'s own
 *  absent value — the line is then not rendered at all. */
function sessionMeta(
  durationMinutes: number | null | undefined,
  location: string | null | undefined,
): string | undefined {
  const parts = [
    typeof durationMinutes === "number" ? `${durationMinutes} min` : undefined,
    location ? LOCATION_LABEL[location] : undefined,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * The upcoming-session card, or NOTHING when no session is booked.
 *
 * Shared by S1 (welcome) and S11 (homebase) so the two stay identical.
 *
 * IT USED TO RENDER NULL-STATE COPY: a dashed box reading "Looks like you still
 * need to book your first session w/ {therapist}". That was operator-approved and
 * is deliberately removed, because on S1 it was not an empty state, it was a
 * false one. S1 is always PRE-BIND, and `coupleFromPreview` hard-codes
 * `firstSessionAt: null` and `therapistName: "your therapist"` there: the
 * least-privilege preview withholds person-bound data until a partner is
 * selected. So the box told EVERY couple they still needed to book, including
 * the ones with a confirmed session three days out, and named their therapist
 * "your therapist" while doing it.
 *
 * On S11 (post-bind, real data) the null case is genuine, and there the right
 * answer is still silence: a client who has not booked cannot book from this
 * screen, so the line was news they could not act on.
 *
 * Returning null rather than leaving the decision to callers means the false
 * copy cannot come back by someone rendering this in a third place.
 *
 * ── WHY `SlotSummaryCard` AND NOT `SessionCard` ──────────────────────────────
 * The therapist confirms a booking on `SlotConfirmStep`'s summary card, and the
 * couple then read that same session here. Two cards for one session meant the
 * two sides of the same appointment looked like different objects. This is that
 * card, extracted (no markup change) so a screen that is not confirming anything
 * can carry it: `SlotConfirmStep` always renders "What happens next", which would
 * promise an act nobody is about to take on a session already booked.
 *
 * The EYEBROW carries the therapist. It was `subtitle="with {therapist}"` under
 * the time; the card puts the standing fact above it and the time in the
 * headline, which is the order the therapist's own confirmation uses.
 *
 * The DATE WORDING is unchanged: `formatSessionTime` is the same function the
 * card here used before, imported rather than restated, so this swaps the shape
 * and not the sentence the couple reads.
 */
export function FirstSessionModule({
  firstSessionAt,
  therapistName,
  durationMinutes,
  location,
  locationAddress,
}: FirstSessionModuleProps) {
  if (firstSessionAt) {
    return (
      <>
        <SlotSummaryCard
          // `neutral`, not the card's default `new`. That green is how the therapist's
          // confirm screen tells the time they are TAKING from the one they are giving
          // up; this screen states one booked session and offers no alternative, so the
          // colour was answering a question nobody asked.
          eyebrowTone="neutral"
          summary={{
            eyebrow: `With ${therapistName}`,
            primary: formatSessionTime(firstSessionAt),
            secondary: sessionMeta(durationMinutes, location),
          }}
        />
        {location === "in_person" && locationAddress ? (
          <p
            data-testid="first-session-address"
            className="mt-1 text-center [font:var(--type-body)] text-secondary"
          >
            {locationAddress}
          </p>
        ) : null}
      </>
    );
  }
  return null;
}
